import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import api from "@/lib/api";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    // Skip auth check if we're in the OAuth callback flow
    if (window.location.hash?.includes("session_id=")) return;
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout failed:", e);
    }
    setUser(null);
    navigate("/");
  };

  const nav = [
    { to: "/", label: "Home" },
    { to: "/programs", label: "Programs" },
    { to: "/quiz", label: "Find Your Program" },
    { to: "/booking", label: "1:1 Coaching" },
    { to: "/blog", label: "Wisdom" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-ub/85 backdrop-blur-xl border-b border-ub" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_heart-mind-mentor/artifacts/zh1jtyh3_image.png"
            alt="Universal Being · Transformation Catalyst"
            className="h-12 w-12 rounded-full object-cover bg-ub-dark"
          />
          <span className="font-serif text-2xl leading-none hidden sm:inline">Universal Being</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-9">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replaceAll(" ", "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-primary-ub" : "text-ub hover:text-primary-ub"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/dashboard"
                data-testid="header-dashboard-link"
                className="flex items-center gap-2 text-sm"
              >
                {user.picture && (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-ub" />
                )}
                <span className="hidden lg:inline text-ub-2">{user.name?.split(" ")[0]}</span>
              </Link>
              <button
                onClick={logout}
                data-testid="header-logout-btn"
                className="p-2 rounded-full hover:bg-ub-alt transition-colors"
                title="Logout"
              >
                <LogOut size={18} className="text-ub-2" />
              </button>
            </div>
          ) : (
            <button
              onClick={login}
              data-testid="header-login-btn"
              className="hidden md:inline-flex bg-primary-ub text-white hover:bg-[var(--ub-primary-hover)] transition-colors duration-300 rounded-full px-6 py-2.5 text-sm font-medium tracking-wide"
            >
              Sign in
            </button>
          )}
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-full hover:bg-ub-alt"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-ub border-t border-ub" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-4">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-lg ${isActive ? "text-primary-ub" : "text-ub"}`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            {user ? (
              <button onClick={logout} className="text-left text-lg text-primary-ub" data-testid="mobile-logout">Logout</button>
            ) : (
              <button onClick={login} className="text-left text-lg text-primary-ub" data-testid="mobile-login">Sign in with Google</button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
